'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Lock } from 'assets/icons/Lock';
import type { ConsentCategorySelection } from 'lib/consent/policy';
import styles from 'components/shared/CookieConsentBanner.module.css';
import type { GrantedCategories } from './ConsentRuntime';

type PrivacyChoicesDialogProps = {
  open: boolean;
  gpc: boolean;
  granted: GrantedCategories;
  onClose: () => void;
  onSave: (categories: ConsentCategorySelection) => void;
};

export function PrivacyChoicesDialog({
  open,
  gpc,
  granted,
  onClose,
  onSave,
}: PrivacyChoicesDialogProps) {
  const t = useTranslations('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const gpcId = useId();
  const [stats, setStats] = useState(granted.stats);
  const [analytics, setAnalytics] = useState(granted.analytics);
  const [ads, setAds] = useState(granted.ads);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setStats(granted.stats);
      setAnalytics(granted.analytics);
      setAds(gpc ? false : granted.ads);
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, granted.ads, granted.analytics, granted.stats, gpc]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <h2 id={titleId} className={styles.dialogTitle}>
        {t('cookieBanner.dialogTitle')}
      </h2>
      <p className={styles.dialogDescription}>{t('cookieBanner.dialogDescription')}</p>
      <ul className={styles.switchList}>
        <SwitchRow
          label={t('cookieBanner.essentialLabel')}
          description={t('cookieBanner.essentialDescription')}
          checked
          disabled
          locked
        />
        <SwitchRow
          label={t('cookieBanner.statsLabel')}
          description={t('cookieBanner.statsDescription')}
          checked={stats}
          onChange={setStats}
        />
        <SwitchRow
          label={t('cookieBanner.analyticsLabel')}
          description={t('cookieBanner.analyticsDescription')}
          checked={analytics}
          onChange={setAnalytics}
        />
        <SwitchRow
          label={t('cookieBanner.adsLabel')}
          description={t('cookieBanner.adsDescription')}
          checked={gpc ? false : ads}
          disabled={gpc}
          describedBy={gpc ? gpcId : undefined}
          onChange={setAds}
        />
      </ul>
      {gpc ? (
        <p id={gpcId} className={styles.gpcNote}>
          {t('cookieBanner.gpcNote')}
        </p>
      ) : null}
      <div className={styles.dialogActions}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={onClose}
        >
          {t('cookieBanner.cancel')}
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.dialogSave}`}
          onClick={() =>
            onSave({
              stats,
              analytics,
              ads: gpc ? false : ads,
            })
          }
        >
          {t('cookieBanner.save')}
        </button>
      </div>
    </dialog>
  );
}

function SwitchRow({
  label,
  description,
  checked,
  disabled,
  locked,
  describedBy,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  /** Always on. Not part of the saved choice. */
  locked?: boolean;
  describedBy?: string;
  onChange?: (next: boolean) => void;
}) {
  const descriptionId = useId();
  return (
    <li className={styles.switchRow}>
      <span className={styles.switchCopy}>
        <span className={styles.switchLabel}>{label}</span>
        <p id={descriptionId} className={styles.switchDescription}>
          {description}
        </p>
      </span>
      <span className={styles.switchControl}>
        <input
          className={
            locked
              ? `${styles.switchInput} ${styles.switchInputLocked}`
              : styles.switchInput
          }
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-checked={checked}
          aria-label={label}
          aria-describedby={
            describedBy ? `${descriptionId} ${describedBy}` : descriptionId
          }
          onChange={(event) => onChange?.(event.target.checked)}
        />
        {locked ? (
          <span className={styles.switchLock} aria-hidden="true">
            <Lock />
          </span>
        ) : null}
      </span>
    </li>
  );
}
